import React, { useEffect, useState, useCallback } from 'react'
import {
  View,
  Text,
  StyleSheet,
  FlatList,
  TouchableOpacity,
  TextInput,
  Alert,
  Platform,
  Modal,
  RefreshControl } from 'react-native'
import {
  ArrowLeft,
  Plus,
  ChevronDown,
  ChevronRight,
  Pencil,
  Trash2,
  Folder,
  FolderOpen,
  Check,
  X } from 'lucide-react-native'
import { supabase } from '../lib/supabase'
import { useLanguage } from '@fliisi/i18n'
import { NativeStackNavigationProp } from '@react-navigation/native-stack'
import { RouteProp } from '@react-navigation/native'
import { RootStackParamList } from '../types/navigation'
import theme from '../theme'
import {
  Button,
  ButtonText,
  Card,
  EmptyState,
  Input,
  Spinner,
  TopBar,
  XStack,
  YStack } from '@fliisi/pollen'
const merchantColors = {
  primary: '#C07A5F',
  primaryLight: '#D4A088',
  accent: '#748C70' }

interface Props {
  navigation: NativeStackNavigationProp<RootStackParamList, 'CategoryManagement'>
  route: RouteProp<RootStackParamList, 'CategoryManagement'>
}

interface Category {
  id: string
  name_fi: string
  name_en: string | null
  name_sv: string | null
  parent_id: string | null
  icon: string | null
  sort_order: number
  is_active: boolean
  product_count?: number
  children?: Category[]
}

const CATEGORY_ICONS = [
  '👕', '👗', '👖', '👠', '👜', '🎒', '⌚', '💍', '🕶️', '🧢',
  '🪑', '🛋️', '🛏️', '🪴', '🖼️', '💡', '🏺', '🧸', '📚', '🎮',
  '🎸', '🎺', '🎨', '📷', '💻', '📱', '🔧', '🧰', '⚽', '🎿',
  '🚴', '🏕️', '🧶', '✂️', '🍳', '🍽️', '🥘', '☕', '🧴', '💄',
]

export default function CategoryManagementScreen({ navigation, route }: Props) {
  const { storeId } = route.params
  const { t, language } = useLanguage()
  const [categories, setCategories] = useState<Category[]>([])
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set())

  // Edit modal state
  const [modalVisible, setModalVisible] = useState(false)
  const [editingCategory, setEditingCategory] = useState<Category | null>(null)
  const [formData, setFormData] = useState({
    name_fi: '',
    name_en: '',
    icon: '',
    parent_id: null as string | null })
  const [saving, setSaving] = useState(false)
  const [showIconPicker, setShowIconPicker] = useState(false)
  const [showParentPicker, setShowParentPicker] = useState(false)

  useEffect(() => {
    fetchCategories()
  }, [])

  const fetchCategories = async () => {
    try {
      // Fetch categories with product counts
      const { data: cats, error } = await supabase
        .from('categories')
        .select('*')
        .order('sort_order')
        .order('name_fi')

      if (error) throw error

      // Get product counts for each category
      const { data: counts } = await supabase
        .from('products')
        .select('category_id')
        .eq('flea_market_id', storeId)

      const countMap: Record<string, number> = {}
      counts?.forEach(p => {
        if (p.category_id) {
          countMap[p.category_id] = (countMap[p.category_id] || 0) + 1
        }
      })

      // Build hierarchy
      const catsWithCounts = (cats || []).map(c => ({
        ...c,
        product_count: countMap[c.id] || 0 }))

      // Build tree structure
      const rootCategories: Category[] = []
      const childMap: Record<string, Category[]> = {}

      catsWithCounts.forEach(cat => {
        if (cat.parent_id) {
          if (!childMap[cat.parent_id]) childMap[cat.parent_id] = []
          childMap[cat.parent_id].push(cat)
        } else {
          rootCategories.push(cat)
        }
      })

      // Attach children to parents
      rootCategories.forEach(root => {
        root.children = childMap[root.id] || []
      })

      setCategories(rootCategories)
    } catch (error) {
      console.error('Error fetching categories:', error)
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }

  const onRefresh = useCallback(() => {
    setRefreshing(true)
    fetchCategories()
  }, [])

  const showAlert = (title: string, message: string) => {
    if (Platform.OS === 'web') {
      window.alert(`${title}\n\n${message}`)
    } else {
      Alert.alert(title, message)
    }
  }

  const confirmDelete = (category: Category) => {
    const hasProducts = (category.product_count || 0) > 0
    const hasChildren = (category.children?.length || 0) > 0

    if (hasProducts || hasChildren) {
      showAlert(
        t('cannot_delete') || 'Cannot Delete',
        hasProducts
          ? (t('category_has_products') || 'This category has products assigned to it. Please reassign them first.')
          : (t('category_has_children') || 'This category has subcategories. Please delete them first.')
      )
      return
    }

    const doDelete = async () => {
      try {
        const { error } = await supabase
          .from('categories')
          .delete()
          .eq('id', category.id)

        if (error) throw error
        fetchCategories()
      } catch (error: any) {
        showAlert(t('error') || 'Error', error.message)
      }
    }

    if (Platform.OS === 'web') {
      if (window.confirm(t('delete_category_confirm') || 'Delete this category?')) {
        doDelete()
      }
    } else {
      Alert.alert(
        t('delete') || 'Delete',
        t('delete_category_confirm') || 'Delete this category?',
        [
          { text: t('cancel') || 'Cancel', style: 'cancel' },
          { text: t('delete') || 'Delete', style: 'destructive', onPress: doDelete },
        ]
      )
    }
  }

  const openEditModal = (category: Category | null, parentId: string | null = null) => {
    setEditingCategory(category)
    setFormData({
      name_fi: category?.name_fi || '',
      name_en: category?.name_en || '',
      icon: category?.icon || '',
      parent_id: category?.parent_id || parentId })
    setModalVisible(true)
  }

  const saveCategory = async () => {
    if (!formData.name_fi.trim()) {
      showAlert(t('error') || 'Error', t('name_required') || 'Finnish name is required')
      return
    }

    setSaving(true)
    try {
      const categoryData = {
        name_fi: formData.name_fi.trim(),
        name_en: formData.name_en.trim() || null,
        icon: formData.icon || null,
        parent_id: formData.parent_id || null }

      if (editingCategory) {
        const { error } = await supabase
          .from('categories')
          .update(categoryData)
          .eq('id', editingCategory.id)

        if (error) throw error
      } else {
        const { error } = await supabase
          .from('categories')
          .insert(categoryData)

        if (error) throw error
      }

      setModalVisible(false)
      fetchCategories()
    } catch (error: any) {
      showAlert(t('error') || 'Error', error.message)
    } finally {
      setSaving(false)
    }
  }

  const toggleExpanded = (id: string) => {
    const newExpanded = new Set(expandedIds)
    if (newExpanded.has(id)) {
      newExpanded.delete(id)
    } else {
      newExpanded.add(id)
    }
    setExpandedIds(newExpanded)
  }

  const getCategoryName = (cat: Category) => {
    return language === 'fi' ? cat.name_fi : (cat.name_en || cat.name_fi)
  }

  const getParentName = (parentId: string | null) => {
    if (!parentId) return t('no_parent') || 'None (Root category)'
    const flatList = categories.flatMap(c => [c, ...(c.children || [])])
    const parent = flatList.find(c => c.id === parentId)
    return parent ? getCategoryName(parent) : t('no_parent') || 'None'
  }

  const renderCategory = (category: Category, depth: number = 0) => {
    const hasChildren = (category.children?.length || 0) > 0
    const isExpanded = expandedIds.has(category.id)

    return (
      <View key={category.id}>
        <View style={[styles.categoryItem, { marginLeft: depth * 20 }]}>
          {/* Expand/collapse button */}
          <TouchableOpacity
            style={styles.expandButton}
            onPress={() => hasChildren && toggleExpanded(category.id)}
            disabled={!hasChildren}
          >
            {hasChildren ? (
              isExpanded ? (
                <ChevronDown size={14} color={theme.colors.text.secondary} />
              ) : (
                <ChevronRight size={14} color={theme.colors.text.secondary} />
              )
            ) : (
              <View style={styles.expandPlaceholder} />
            )}
          </TouchableOpacity>

          {/* Icon */}
          <View style={styles.categoryIcon}>
            {category.icon ? (
              <Text style={styles.categoryIconText}>{category.icon}</Text>
            ) : (
              <Folder size={20} color={theme.colors.text.secondary} />
            )}
          </View>

          {/* Name and info */}
          <TouchableOpacity
            style={styles.categoryInfo}
            onPress={() => openEditModal(category)}
          >
            <Text style={styles.categoryName}>{getCategoryName(category)}</Text>
            <Text style={styles.categoryMeta}>
              {category.product_count || 0} {t('products') || 'products'}
              {hasChildren && ` • ${category.children?.length} ${t('subcategories') || 'subcategories'}`}
            </Text>
          </TouchableOpacity>

          {/* Actions */}
          <TouchableOpacity
            style={styles.actionButton}
            onPress={() => openEditModal(null, category.id)}
          >
            <Plus size={16} color={merchantColors.primary} />
          </TouchableOpacity>

          <TouchableOpacity
            style={styles.actionButton}
            onPress={() => openEditModal(category)}
          >
            <Pencil size={16} color={theme.colors.text.secondary} />
          </TouchableOpacity>

          <TouchableOpacity
            style={styles.actionButton}
            onPress={() => confirmDelete(category)}
          >
            <Trash2 size={16} color={theme.colors.text.secondary} style={{ opacity: 0.6 }} />
          </TouchableOpacity>
        </View>

        {/* Children */}
        {isExpanded && category.children?.map(child => renderCategory(child, depth + 1))}
      </View>
    )
  }

  const renderIconPicker = () => (
    <Modal visible={showIconPicker} animationType="slide" transparent>
      <View style={styles.pickerModalOverlay}>
        <View style={styles.pickerModalContent}>
          <View style={styles.pickerModalHeader}>
            <Text style={styles.pickerModalTitle}>{t('select_icon') || 'Select Icon'}</Text>
            <TouchableOpacity onPress={() => setShowIconPicker(false)} style={styles.pickerModalCloseButton}>
              <X size={24} color={theme.colors.text.secondary} />
            </TouchableOpacity>
          </View>
          <View style={styles.iconGrid}>
            {CATEGORY_ICONS.map(icon => (
              <TouchableOpacity
                key={icon}
                style={[styles.iconOption, formData.icon === icon && styles.iconOptionSelected]}
                onPress={() => {
                  setFormData(prev => ({ ...prev, icon }))
                  setShowIconPicker(false)
                }}
              >
                <Text style={styles.iconOptionText}>{icon}</Text>
              </TouchableOpacity>
            ))}
          </View>
          <TouchableOpacity
            style={styles.clearIconButton}
            onPress={() => {
              setFormData(prev => ({ ...prev, icon: '' }))
              setShowIconPicker(false)
            }}
          >
            <Text style={styles.clearIconText}>{t('no_icon') || 'No icon'}</Text>
          </TouchableOpacity>
        </View>
      </View>
    </Modal>
  )

  const renderParentPicker = () => {
    const flatList = categories.flatMap(c => [c, ...(c.children || [])])
    const availableParents = flatList.filter(c => c.id !== editingCategory?.id)

    return (
      <Modal visible={showParentPicker} animationType="slide" transparent>
        <View style={styles.pickerModalOverlay}>
          <View style={styles.pickerModalContent}>
            <View style={styles.pickerModalHeader}>
              <Text style={styles.pickerModalTitle}>{t('select_parent') || 'Select Parent'}</Text>
              <TouchableOpacity onPress={() => setShowParentPicker(false)} style={styles.pickerModalCloseButton}>
                <X size={24} color={theme.colors.text.secondary} />
              </TouchableOpacity>
            </View>
            <FlatList
              data={[{ id: null, name_fi: t('no_parent') || 'None (Root)', name_en: null, icon: null } as any, ...availableParents]}
              keyExtractor={item => item.id || 'none'}
              renderItem={({ item }) => (
                <TouchableOpacity
                  style={[styles.parentOption, formData.parent_id === item.id && styles.parentOptionSelected]}
                  onPress={() => {
                    setFormData(prev => ({ ...prev, parent_id: item.id }))
                    setShowParentPicker(false)
                  }}
                >
                  {item.icon && <Text style={styles.parentOptionIcon}>{item.icon}</Text>}
                  <Text style={styles.parentOptionText}>
                    {item.id ? getCategoryName(item) : (t('no_parent') || 'None (Root)')}
                  </Text>
                  {formData.parent_id === item.id && <Check size={18} color={merchantColors.primary} />}
                </TouchableOpacity>
              )}
            />
          </View>
        </View>
      </Modal>
    )
  }

  const renderEditModal = () => (
    <Modal visible={modalVisible} animationType="slide" transparent>
      <View style={styles.modalOverlay}>
        <View style={styles.modalContent}>
          <View style={styles.modalHeader}>
            <Text style={styles.modalTitle}>
              {editingCategory ? (t('edit_category') || 'Edit Category') : (t('new_category') || 'New Category')}
            </Text>
            <TouchableOpacity onPress={() => setModalVisible(false)} style={styles.modalCloseButton}>
              <X size={24} color={theme.colors.text.secondary} />
            </TouchableOpacity>
          </View>

          <View style={styles.modalBody}>
            {/* Icon picker */}
            <View style={styles.inputGroup}>
              <Text style={styles.label}>{t('icon') || 'Icon'}</Text>
              <TouchableOpacity style={styles.iconPicker} onPress={() => setShowIconPicker(true)}>
                {formData.icon ? (
                  <Text style={styles.iconPickerText}>{formData.icon}</Text>
                ) : (
                  <Folder size={32} color={theme.colors.text.secondary} />
                )}
                <Text style={styles.iconPickerLabel}>{t('tap_to_change') || 'Tap to change'}</Text>
              </TouchableOpacity>
            </View>

            {/* Name (Finnish) */}
            <View style={styles.inputGroup}>
              <Text style={styles.label}>{t('name_fi') || 'Name (Finnish)'} *</Text>
              <TextInput
                style={styles.input}
                value={formData.name_fi}
                onChangeText={v => setFormData(prev => ({ ...prev, name_fi: v }))}
                placeholder="Vaatteet"
                placeholderTextColor={theme.colors.text.secondary}
              />
            </View>

            {/* Name (English) */}
            <View style={styles.inputGroup}>
              <Text style={styles.label}>{t('name_en') || 'Name (English)'}</Text>
              <TextInput
                style={styles.input}
                value={formData.name_en}
                onChangeText={v => setFormData(prev => ({ ...prev, name_en: v }))}
                placeholder="Clothing"
                placeholderTextColor={theme.colors.text.secondary}
              />
            </View>

            {/* Parent category */}
            <View style={styles.inputGroup}>
              <Text style={styles.label}>{t('parent_category') || 'Parent Category'}</Text>
              <TouchableOpacity style={styles.picker} onPress={() => setShowParentPicker(true)}>
                <Text style={styles.pickerText}>{getParentName(formData.parent_id)}</Text>
                <ChevronRight size={16} color={theme.colors.text.secondary} />
              </TouchableOpacity>
            </View>
          </View>

          <View style={styles.modalActions}>
            <TouchableOpacity
              style={[styles.saveButton, saving && styles.buttonDisabled]}
              onPress={saveCategory}
              disabled={saving}
            >
              {saving ? (
                <Spinner color="#fff" />
              ) : (
                <Text style={styles.saveButtonText}>{t('save') || 'Save'}</Text>
              )}
            </TouchableOpacity>

            <Button variant="outline" onPress={() => setModalVisible(false)}><ButtonText>{t('cancel') || 'Cancel'}</ButtonText></Button>
          </View>
        </View>
      </View>
    </Modal>
  )

  if (loading) {
    return (
      <YStack f={1} jc="center" ai="center"><Spinner size="large" /></YStack>
    )
  }

  return (
    <View style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backButtonContainer}>
          <ArrowLeft size={18} color={merchantColors.primary} />
          <Text style={styles.backButton}>{t('back') || 'Back'}</Text>
        </TouchableOpacity>
        <Text style={styles.headerTitle}>{t('categories') || 'Categories'}</Text>
        <TouchableOpacity style={styles.addButton} onPress={() => openEditModal(null)}>
          <Plus size={20} color="#fff" />
        </TouchableOpacity>
      </View>

      {/* Categories List */}
      <FlatList
        data={categories}
        keyExtractor={item => item.id}
        renderItem={({ item }) => renderCategory(item)}
        contentContainerStyle={styles.listContent}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} colors={[merchantColors.primary]} />
        }
        ListEmptyComponent={
          <View style={styles.emptyState}>
            <View style={styles.emptyIconContainer}>
              <FolderOpen size={64} color={theme.colors.text.secondary} />
            </View>
            <Text style={styles.emptyText}>{t('no_categories') || 'No categories yet'}</Text>
            <Text style={styles.emptyHint}>{t('add_category_hint') || 'Tap + to create your first category'}</Text>
          </View>
        }
      />

      {/* Modals */}
      {renderEditModal()}
      {renderIconPicker()}
      {renderParentPicker()}
</View>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.background.primary },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center' },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: theme.spacing.md,
    paddingTop: 16,
    paddingBottom: theme.spacing.md,
    backgroundColor: theme.colors.background.card,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.ui.borderLight },
  backButtonContainer: {
    flexDirection: 'row',
    alignItems: 'center' },
  backButton: {
    color: merchantColors.primary,
    fontSize: 16,
    fontWeight: '500',
    marginLeft: 4 },
  headerTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: theme.colors.text.primary },
  addButton: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: merchantColors.primary,
    justifyContent: 'center',
    alignItems: 'center' },
  listContent: {
    padding: theme.spacing.md,
    paddingBottom: 120 },
  categoryItem: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: theme.colors.background.card,
    borderRadius: theme.borderRadius.md,
    padding: 12,
    marginBottom: 8,
    ...theme.shadows.small },
  expandButton: {
    width: 24,
    height: 24,
    justifyContent: 'center',
    alignItems: 'center' },
  expandPlaceholder: {
    width: 10 },
  categoryIcon: {
    width: 40,
    height: 40,
    borderRadius: 8,
    backgroundColor: theme.colors.background.secondary,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12 },
  categoryIconText: {
    fontSize: 20 },
  categoryInfo: {
    flex: 1 },
  categoryName: {
    fontSize: 16,
    fontWeight: '600',
    color: theme.colors.text.primary },
  categoryMeta: {
    fontSize: 12,
    color: theme.colors.text.secondary,
    marginTop: 2 },
  actionButton: {
    width: 32,
    height: 32,
    justifyContent: 'center',
    alignItems: 'center',
    marginLeft: 4 },
  emptyState: {
    alignItems: 'center',
    paddingVertical: 60,
    paddingHorizontal: theme.spacing.xl },
  emptyIconContainer: {
    marginBottom: theme.spacing.md },
  emptyText: {
    fontSize: 18,
    fontWeight: '600',
    color: theme.colors.text.primary,
    marginBottom: theme.spacing.sm },
  emptyHint: {
    fontSize: 14,
    color: theme.colors.text.secondary,
    textAlign: 'center' },
  // Modal styles
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'flex-end' },
  modalContent: {
    backgroundColor: theme.colors.background.card,
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
    maxHeight: '80%' },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.ui.borderLight },
  modalTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: theme.colors.text.primary },
  modalCloseButton: {
    padding: 4 },
  modalBody: {
    padding: theme.spacing.md },
  inputGroup: {
    marginBottom: 20 },
  label: {
    fontSize: 14,
    fontWeight: '600',
    color: theme.colors.text.primary,
    marginBottom: 8 },
  input: {
    backgroundColor: theme.colors.background.secondary,
    borderRadius: theme.borderRadius.md,
    padding: 14,
    fontSize: 16,
    borderWidth: 2,
    borderColor: theme.colors.ui.border,
    color: theme.colors.text.primary },
  iconPicker: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: theme.colors.background.secondary,
    borderRadius: theme.borderRadius.md,
    padding: 14,
    borderWidth: 2,
    borderColor: theme.colors.ui.border },
  iconPickerText: {
    fontSize: 32,
    marginRight: 12 },
  iconPickerLabel: {
    fontSize: 14,
    color: theme.colors.text.secondary },
  picker: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: theme.colors.background.secondary,
    borderRadius: theme.borderRadius.md,
    padding: 14,
    borderWidth: 2,
    borderColor: theme.colors.ui.border },
  pickerText: {
    fontSize: 16,
    color: theme.colors.text.primary },
  modalActions: {
    padding: theme.spacing.md,
    gap: 12 },
  saveButton: {
    backgroundColor: merchantColors.primary,
    borderRadius: theme.borderRadius.md,
    padding: 16,
    alignItems: 'center' },
  saveButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600' },
  cancelButton: {
    padding: 16,
    alignItems: 'center' },
  cancelButtonText: {
    color: theme.colors.text.secondary,
    fontSize: 16 },
  buttonDisabled: {
    opacity: 0.6 },
  // Icon picker modal
  pickerModalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    padding: theme.spacing.lg },
  pickerModalContent: {
    backgroundColor: theme.colors.background.card,
    borderRadius: theme.borderRadius.lg,
    maxHeight: '80%' },
  pickerModalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.ui.borderLight },
  pickerModalTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: theme.colors.text.primary },
  pickerModalCloseButton: {
    padding: 4 },
  iconGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    padding: theme.spacing.md,
    justifyContent: 'center' },
  iconOption: {
    width: 50,
    height: 50,
    borderRadius: 8,
    justifyContent: 'center',
    alignItems: 'center',
    margin: 4,
    backgroundColor: theme.colors.background.secondary },
  iconOptionSelected: {
    backgroundColor: merchantColors.primaryLight,
    borderWidth: 2,
    borderColor: merchantColors.primary },
  iconOptionText: {
    fontSize: 24 },
  clearIconButton: {
    padding: theme.spacing.md,
    alignItems: 'center',
    borderTopWidth: 1,
    borderTopColor: theme.colors.ui.borderLight },
  clearIconText: {
    fontSize: 14,
    color: merchantColors.primary },
  parentOption: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: theme.spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.ui.borderLight },
  parentOptionSelected: {
    backgroundColor: `${merchantColors.primary}10` },
  parentOptionIcon: {
    fontSize: 20,
    marginRight: 12 },
  parentOptionText: {
    flex: 1,
    fontSize: 16,
    color: theme.colors.text.primary } })
